import * as React from "react"
import { useApplicationsPage } from "../../../hooks/applications/useApplicationsPage"
import { Helmet } from "react-helmet"
import HeroSection from "../../contentful/HeroSection"
import { MetadataModel } from "../../contentful/models"
import RichText from "../../molecules/RichText"
import PageContentWrapper from "../../organisms/PageContentWrapper"
import ResourceShowcase from "../../contentful/ResourceShowcase"
import ColorfulH1 from "../../atoms/typography/ColorfulH1"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import ContactUsForm from "../../organisms/ContactUsForm"
import { theme } from "../../theme"
import FormSection from "../../contentful/FormSection"
import { useContentfulInspectorMode, useContentfulLiveUpdates } from "@contentful/live-preview/react"
import { ContentfulLiveUpdates } from "../../../graphql/contentful/client"
import LoadingSpinner from "../../atoms/LoadingSpinner"

const Metadata: React.FC<MetadataModel & { imageUrl?: string }> = ({ title, description, type, imageUrl }) => {
  const url = window.location.href
  return (
    <Helmet>
      <meta property="og:title" content={title} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      {description && <meta property="og:description" content={description} />}
      <title>{title} | Satcube.com</title>
    </Helmet>
  )
}

const ApplicationsPage: React.FC = () => {
  const { applications: unliveApplications, loading, error } = useApplicationsPage()
  const applications = useContentfulLiveUpdates(unliveApplications)
  const inspectorProps = useContentfulInspectorMode({ entryId: applications?.sys?.id })

  if (error) {
    return <h1>{error?.message}</h1>
  }

  if (loading || !applications) {
    return (
      <div>
        <HeroSection />
        <PageContentWrapper>
          <HorizontalSpacer spacing={2} />
          <LoadingSpinner />
          <HorizontalSpacer spacing={2} />
        </PageContentWrapper>
      </div>
    )
  }

  const { metadata, hero, title, content, casesCollection } = applications

  return (
    <div style={{ backgroundColor: theme.colors.greyLight }}>
      <Metadata {...metadata} />
      <div>
        <HeroSection {...hero} />
        <PageContentWrapper>
          <HorizontalSpacer spacing={3} />
          <ColorfulH1 color="blue" bold {...inspectorProps({ fieldId: "title" })}>
            {title}
          </ColorfulH1>
          <HorizontalSpacer spacing={2} />
        </PageContentWrapper>
        <RichText rtf={content} />
        <HorizontalSpacer spacing={2} />
        <ResourceShowcase __typename="ResourceShowcase" resourcesCollection={casesCollection} />
        <HorizontalSpacer spacing={3} />
      </div>
      <FormSection bgColor={theme.colors.white}>
        <ContactUsForm />
      </FormSection>
    </div>
  )
}

const LiveApplicationsPage = () => (
  <ContentfulLiveUpdates>
    <ApplicationsPage />
  </ContentfulLiveUpdates>
)

export default LiveApplicationsPage
